/**
 * HealthAlertBanner — public dismissible banner showing the single
 * currently-active global health alert (e.g. WHO heatwave guidance,
 * NHS flu reminder). Renders nothing if no alert is active.
 *
 * Mounted on Landing.jsx and Dashboard.jsx for the beta MVP. Dismissal
 * is per-alert-per-device — stored in localStorage so refreshes don't
 * re-show a banner the user has already acknowledged, but a new
 * published alert (different id) will show again.
 *
 * Mobile-responsive: collapses to stacked layout below sm breakpoint;
 * link, dismiss-X, and body remain readable on a 360px viewport.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Info, AlertTriangle, ShieldAlert, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_THEME = {
  info: {
    wrap: 'bg-sky-50 border-sky-200 text-sky-900',
    title: 'text-sky-900',
    icon: Info,
    iconColor: 'text-sky-700',
    accent: 'bg-sky-100',
  },
  warning: {
    wrap: 'bg-amber-50 border-amber-300 text-amber-900',
    title: 'text-amber-900',
    icon: AlertTriangle,
    iconColor: 'text-amber-700',
    accent: 'bg-amber-100',
  },
  critical: {
    wrap: 'bg-rose-50 border-rose-300 text-rose-900',
    title: 'text-rose-900',
    icon: ShieldAlert,
    iconColor: 'text-rose-700',
    accent: 'bg-rose-100',
  },
};

const dismissKey = (id) => `hl_alert_dismissed_${id}`;

const HealthAlertBanner = () => {
  const [alert, setAlert] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/health-alerts/active`);
        if (cancelled) return;
        const a = res?.data?.alert;
        if (a && a.id) {
          // Skip the banner if this device has already dismissed this id.
          const seen = typeof window !== 'undefined' && window.localStorage
            ? window.localStorage.getItem(dismissKey(a.id))
            : null;
          if (!seen) setAlert(a);
        }
      } catch {
        // Silent — banner is non-essential UI; never block the page.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!alert || dismissed) return null;

  const theme = SEVERITY_THEME[alert.severity] || SEVERITY_THEME.info;
  const Icon = theme.icon;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(dismissKey(alert.id), '1');
      }
    } catch { /* private-mode / quota — ignore */ }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`border ${theme.wrap} rounded-2xl px-4 py-3 sm:py-3.5 shadow-sm mb-4`}
      data-testid="health-alert-banner"
      data-alert-id={alert.id}
      data-severity={alert.severity}
    >
      <div className="flex items-start gap-3">
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${theme.accent}`}>
          <Icon className={`h-4 w-4 ${theme.iconColor}`} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p
              className={`text-sm font-semibold leading-snug ${theme.title}`}
              data-testid="health-alert-title"
            >
              {alert.title}
            </p>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-bold bg-white/60">
              {alert.severity}
            </span>
          </div>
          <p
            className="text-xs sm:text-sm mt-1 leading-relaxed whitespace-pre-line"
            data-testid="health-alert-body"
          >
            {alert.body}
          </p>
          {alert.link_url && (
            <a
              href={alert.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold mt-2 underline underline-offset-2 hover:opacity-80"
              data-testid="health-alert-link"
            >
              {alert.link_label || 'Learn more'}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss alert"
          className="p-1 rounded-md hover:bg-white/40 focus:outline-none focus:ring-2 focus:ring-current/30 flex-shrink-0"
          data-testid="health-alert-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default HealthAlertBanner;
